import React from 'react'
import cookie from 'isomorphic-cookie'
import { isNil } from 'lodash'
import { useFeature, useFlag } from 'toggled'

import { Price, SignupToSeePricesForm } from '~/components'
import { getPriceFromTier } from '~/components/product/shared'
import ShippingSelectorModal from '~/components/shipping-modal'
import { useFavoriteProduct, useMultiDistributorSession, useUser } from '~/hooks'
import useCartProductQuantity from '~/hooks/use-cart-product-quantity'
import useModalManager from '~/hooks/use-modal-manager'
import Button from '~/truck/button'
import theme from '~/truck/theme'
import type { ILineItem } from '~/types/line-items'
import type { IProduct } from '~/types/products'
import { redirectTo } from '~/utils'
import { cookieNames, flags } from '~/utils/constants'

import VariantSelector from '../variant-selector'

import { StyledAddToFavoritesButton, StyledProductAttributes } from './cta.style'
import { CTABasicContent } from './cta-basic-content'
import { CTACounter } from './cta-counter'

// TODO: Assign the types of props
export const CTA = (props: {
  activeVariant: IProduct & ILineItem
  product: any
  fallbackProduct: any
  variants: any[]
  onVariantChange: (variant: any) => void
}) => {
  const { product, activeVariant } = props

  const { openModal } = useModalManager()

  const showMyFavorites = useFlag(flags.MY_FAVORITES)

  const showPriceAfterLoginFeature = useFeature(flags.SHOW_PRICE_AFTER_LOGIN)

  const shippingBeforeBuyFF = useFeature(flags.SHIPPING_BEFORE_BUY)

  const { multiDistributorSession } = useMultiDistributorSession()

  const user = useUser()

  const hasAdvancedFiltersFF = useFlag(flags.ADVANCED_FILTERS)

  const cartQuantity = useCartProductQuantity(activeVariant?.id)

  const getPriceVisibility = () => {
    const { activeVariant } = props

    return activeVariant ? 'visible' : 'hidden'
  }

  const onSeePriceClick = (productCallbackURL: string) => {
    if (shippingBeforeBuyFF?.settings?.hidePrices && !multiDistributorSession) {
      openModal(ShippingSelectorModal)

      return
    }

    if (showPriceAfterLoginFeature?.settings?.legacy) {
      openModal(SignupToSeePricesForm)

      return
    }
    cookie.save(cookieNames.CALLBACK_URL, productCallbackURL)
    redirectTo('/ingresar')
  }

  const renderContent = () => {
    const { fallbackProduct, onVariantChange, variants } = props

    const pricing = activeVariant?.pricing

    const isVisitor = !user.isLoggedIn

    const hidePrice =
      (showPriceAfterLoginFeature && isVisitor) ||
      (!!shippingBeforeBuyFF?.settings?.hidePrices && !multiDistributorSession)

    const shouldShowProductAttributes =
      !fallbackProduct.hasVariants && !isNil(product.attributes) && hasAdvancedFiltersFF

    const price = getPriceFromTier({
      ...(activeVariant ?? {}),
      price: pricing?.price,
      quantity: cartQuantity || 0,
    })

    const productUrl = `/p/${props.activeVariant?.slug}`

    return (
      <>
        {!hidePrice ? (
          <Price
            allowTaxLabel
            allowDiscountLabel
            oldValue={pricing?.oldPrice}
            value={price}
            textStyle="h3Semibold"
            style={{
              visibility: getPriceVisibility(),
              marginBottom: theme.spacing.cozy * 5 + 2,
            }}
            isTaxExempt={pricing?.isTaxExempt}
            tierPricing={activeVariant?.tierPricing}
            salesUnitFactor={activeVariant?.salesUnitFactor}
          />
        ) : (
          <Button
            style={{ margin: `${theme.spacing.compact * 5}px 0px` }}
            color="secondary"
            onClick={() => onSeePriceClick(productUrl)}
          >
            Ver Precio
          </Button>
        )}
        {shouldShowProductAttributes && <StyledProductAttributes isCollapsible attributes={product.attributes} />}
        {fallbackProduct.hasVariants && (
          <VariantSelector
            fallbackProduct={fallbackProduct}
            activeVariant={activeVariant}
            onChange={onVariantChange}
            variants={variants}
            style={{
              marginBottom: theme.spacing.compact * 5,
            }}
          />
        )}
        {!hidePrice && <CTACounter activeVariant={activeVariant} />}
      </>
    )
  }

  const { fallbackProduct, variants } = props

  const { onToggleFavorite } = useFavoriteProduct(activeVariant ?? ({} as IProduct))

  const name = activeVariant?.name ?? fallbackProduct.name

  const presentation = activeVariant?.presentation ?? fallbackProduct.presentation

  const sku = activeVariant?.sku ?? fallbackProduct.sku

  return (
    <CTABasicContent
      activeVariant={activeVariant}
      name={name}
      presentation={presentation}
      sku={sku}
      variants={variants}
      fallbackProduct={fallbackProduct}
    >
      {renderContent()}
      {showMyFavorites ? (
        <StyledAddToFavoritesButton
          $visible={!!activeVariant}
          onClick={onToggleFavorite}
          favoriteSince={activeVariant?.favoriteSince}
        />
      ) : null}
    </CTABasicContent>
  )
}
